import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  Label,
  Spinner,
} from 'reactstrap';
import { uploadWhatsappFile } from 'services/whatsapp';

const MESSAGE_TYPES = [
  { value: 'CHARGE',   label: 'Cobrança'      },
  { value: 'REMINDER', label: 'Lembrete'      },
  { value: 'CUSTOM',   label: 'Personalizada' },
];

const CHARGE_TEMPLATE = (name, value) =>
  `Olá ${name}, passando para informar que você possui um débito de R$ ${value} em aberto. Por favor, entre em contato conosco para regularizar. Obrigado!`;

const REMINDER_TEMPLATE = (name) =>
  `Olá ${name}, este é um lembrete da nossa equipe. Caso tenha alguma dúvida, entre em contato conosco!`;

const initialState = {
  clientId: '',
  messageBody: '',
  messageType: 'CUSTOM',
  scheduledAt: '',
  attachmentUrl: '',
  attachmentType: '',
  attachmentName: '',
};

// --------------------------------------------------------------------------
// ClientSearchInput — combobox com filtro de cidade + busca por nome
// --------------------------------------------------------------------------
function ClientSearchInput({ clients, value, onChange }) {
  const [citySearch, setCitySearch]     = useState('');
  const [cityFilter, setCityFilter]     = useState('');
  const [nameSearch, setNameSearch]     = useState('');
  const [cityOpen, setCityOpen]         = useState(false);
  const [open, setOpen]                 = useState(false);
  const wrapperRef                      = useRef(null);
  const cityRef                         = useRef(null);

  // Fecha ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setCityOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Lista única de cidades filtrada pelo que o usuário digita
  const cities = useMemo(() => {
    const set = new Set(clients.map((c) => c.city).filter(Boolean));
    return Array.from(set).sort().filter((c) =>
      !citySearch || c.toLowerCase().includes(citySearch.toLowerCase())
    );
  }, [clients, citySearch]);

  // Clientes filtrados por cidade selecionada e nome
  const filtered = useMemo(() => {
    return clients
      .filter((c) => !cityFilter || c.city === cityFilter)
      .filter((c) => !nameSearch || c.name.toLowerCase().includes(nameSearch.toLowerCase()));
  }, [clients, cityFilter, nameSearch]);

  const selected = clients.find((c) => String(c.id) === String(value));

  const handleSelect = (client) => {
    onChange(client);
    setOpen(false);
    setNameSearch('');
  };

  const handleClear = () => {
    onChange(null);
    setNameSearch('');
    setCityFilter('');
    setCitySearch('');
  };

  const handleCitySelect = (city) => {
    setCityFilter(city);
    setCitySearch(city);
    setCityOpen(false);
  };

  const handleCityClear = (e) => {
    e.stopPropagation();
    setCityFilter('');
    setCitySearch('');
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {/* Trigger */}
      <div
        onClick={() => { setOpen((o) => !o); setCityOpen(false); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #dee2e6',
          borderRadius: '0.375rem',
          padding: '0.45rem 0.75rem',
          background: '#fff',
          cursor: 'pointer',
          minHeight: '38px',
          userSelect: 'none',
        }}
      >
        <span style={{ color: selected ? '#32325d' : '#adb5bd', fontSize: '14px' }}>
          {selected
            ? `${selected.name}${selected.city ? ` — ${selected.city}` : ''}${selected.phoneNumber ? ` (${selected.phoneNumber})` : ''}`
            : 'Selecione um cliente...'}
        </span>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {selected && (
            <span
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
              style={{ color: '#adb5bd', fontSize: '16px', lineHeight: 1, cursor: 'pointer' }}
              title='Limpar'
            >
              ×
            </span>
          )}
          <span style={{ color: '#adb5bd', fontSize: '12px' }}>▼</span>
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          zIndex: 1050,
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: '0.375rem',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          maxHeight: '320px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Filtros */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '6px' }}>
            {/* Busca de cidade com dropdown próprio */}
            <div ref={cityRef} style={{ position: 'relative', flex: '0 0 auto', width: '160px' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type='text'
                  placeholder='Filtrar cidade...'
                  value={citySearch}
                  onChange={(e) => { setCitySearch(e.target.value); setCityOpen(true); if (!e.target.value) setCityFilter(''); }}
                  onFocus={() => setCityOpen(true)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    fontSize: '13px',
                    border: `1px solid ${cityFilter ? '#28a745' : '#dee2e6'}`,
                    borderRadius: '0.25rem',
                    padding: '4px 24px 4px 8px',
                    outline: 'none',
                    background: cityFilter ? '#f0fff4' : '#f8f9fa',
                  }}
                />
                {cityFilter && (
                  <span
                    onClick={handleCityClear}
                    style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#adb5bd', fontSize: '14px' }}
                  >
                    ×
                  </span>
                )}
              </div>
              {cityOpen && cities.length > 0 && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 2px)',
                    left: 0,
                    right: 0,
                    background: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.25rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    maxHeight: '180px',
                    overflowY: 'auto',
                    zIndex: 1100,
                  }}
                >
                  <div
                    onClick={() => { setCityFilter(''); setCitySearch(''); setCityOpen(false); }}
                    style={{ padding: '6px 10px', fontSize: '12px', color: '#adb5bd', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    Todas as cidades
                  </div>
                  {cities.map((city) => (
                    <div
                      key={city}
                      onClick={() => handleCitySelect(city)}
                      style={{
                        padding: '6px 10px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        background: cityFilter === city ? '#f0fff4' : 'transparent',
                        fontWeight: cityFilter === city ? 600 : 400,
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.background = cityFilter === city ? '#f0fff4' : 'transparent'}
                    >
                      {city}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <input
              autoFocus
              type='text'
              placeholder='Buscar por nome...'
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1,
                fontSize: '13px',
                border: '1px solid #dee2e6',
                borderRadius: '0.25rem',
                padding: '4px 8px',
                outline: 'none',
              }}
            />
          </div>

          {/* Lista */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#adb5bd', fontSize: '13px' }}>
                Nenhum cliente encontrado
              </div>
            ) : (
              filtered.map((c) => (
                <div
                  key={c.id}
                  onClick={() => handleSelect(c)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    background: String(c.id) === String(value) ? '#f0f8ff' : 'transparent',
                    borderBottom: '1px solid #f8f9fa',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = String(c.id) === String(value) ? '#f0f8ff' : 'transparent'}
                >
                  <span style={{ fontWeight: String(c.id) === String(value) ? 600 : 400 }}>
                    {c.name}
                  </span>
                  <span style={{ color: '#adb5bd', fontSize: '12px' }}>
                    {[c.city, c.phoneNumber].filter(Boolean).join(' · ')}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Contador */}
          <div style={{ padding: '4px 12px', borderTop: '1px solid #f0f0f0', fontSize: '11px', color: '#adb5bd' }}>
            {filtered.length} cliente{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Modal principal
// --------------------------------------------------------------------------
const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function WhatsappMessageModal({ isOpen, toggle, onSave, clients, initialValues }) {
  const [form, setForm]                     = useState(initialState);
  const [showAttachment, setShowAttachment] = useState(false);
  const [sendNow, setSendNow]               = useState(false);
  const [uploading, setUploading]           = useState(false);
  const fileInputRef                        = useRef(null);

  useEffect(() => {
    if (isOpen && initialValues) {
      setForm((prev) => ({
        ...prev,
        clientId:       initialValues.clientId       || '',
        messageBody:    initialValues.messageBody    || '',
        attachmentUrl:  initialValues.attachmentUrl  || '',
        attachmentType: initialValues.attachmentType || '',
        attachmentName: initialValues.attachmentName || '',
      }));
      if (initialValues.attachmentUrl) setShowAttachment(true);
    }
    if (!isOpen) {
      setForm(initialState);
      setShowAttachment(false);
      setSendNow(false);
      setUploading(false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      alert('Apenas arquivos PDF ou imagens (JPEG, PNG, WebP) são aceitos.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    const result = await uploadWhatsappFile(file);
    setUploading(false);
    if (result) {
      setForm((prev) => ({
        ...prev,
        attachmentUrl:  result.url,
        attachmentType: result.mimeType,
        attachmentName: result.filename,
      }));
    }
    e.target.value = '';
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    const client = clients.find((c) => String(c.id) === String(form.clientId));
    let body = form.messageBody;
    if (type === 'CHARGE'   && client) body = CHARGE_TEMPLATE(client.name, '0,00');
    if (type === 'REMINDER' && client) body = REMINDER_TEMPLATE(client.name);
    setForm((prev) => ({ ...prev, messageType: type, messageBody: body }));
  };

  const handleClientSelect = (client) => {
    if (!client) {
      setForm((prev) => ({ ...prev, clientId: '' }));
      return;
    }
    let body = form.messageBody;
    if (form.messageType === 'CHARGE')   body = CHARGE_TEMPLATE(client.name, '0,00');
    if (form.messageType === 'REMINDER') body = REMINDER_TEMPLATE(client.name);
    setForm((prev) => ({ ...prev, clientId: String(client.id), messageBody: body }));
  };

  const isValid = form.clientId && form.messageBody && (sendNow || form.scheduledAt);

  const handleSubmit = (forceNow = false) => {
    if (!isValid) return;
    const now = new Date();
    // Formata como yyyy-MM-ddTHH:mm:ss para o backend
    const pad = (n) => String(n).padStart(2, '0');
    const localNow = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    onSave({
      clientId:       Number(form.clientId),
      messageBody:    form.messageBody,
      messageType:    form.messageType,
      scheduledAt:    sendNow ? localNow : form.scheduledAt,
      attachmentUrl:  showAttachment ? form.attachmentUrl  : null,
      attachmentType: showAttachment ? form.attachmentType : null,
      attachmentName: showAttachment ? form.attachmentName : null,
      sendNow,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size='lg'>
      <ModalHeader toggle={toggle}>Nova Mensagem WhatsApp</ModalHeader>
      <ModalBody>
        <Form>
          <Row>
            <Col md='6'>
              <FormGroup>
                <Label>Cliente</Label>
                <ClientSearchInput
                  clients={clients}
                  value={form.clientId}
                  onChange={handleClientSelect}
                />
              </FormGroup>
            </Col>
            <Col md='3'>
              <FormGroup>
                <Label>Tipo</Label>
                <Input
                  type='select'
                  name='messageType'
                  value={form.messageType}
                  onChange={handleTypeChange}
                >
                  {MESSAGE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md='3'>
              <FormGroup>
                <Label>Data e Hora do Envio</Label>
                {sendNow ? (
                  <div
                    style={{
                      padding: '7px 12px',
                      background: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '0.375rem',
                      fontSize: '13px',
                      color: '#155724',
                      fontWeight: 600,
                    }}
                  >
                    Envio imediato
                  </div>
                ) : (
                  <Input
                    type='datetime-local'
                    name='scheduledAt'
                    value={form.scheduledAt}
                    onChange={handleChange}
                  />
                )}
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>Mensagem</Label>
            <Input
              type='textarea'
              name='messageBody'
              rows='5'
              placeholder='Digite sua mensagem...'
              value={form.messageBody}
              onChange={handleChange}
            />
          </FormGroup>

          <div className='d-flex align-items-center mb-3' style={{ gap: '20px' }}>
            <div className='d-flex align-items-center'>
              <Input
                type='checkbox'
                id='sendNowCheck'
                style={{ position: 'relative', marginLeft: 0 }}
                checked={sendNow}
                onChange={(e) => setSendNow(e.target.checked)}
              />
              <Label for='sendNowCheck' className='mb-0 ml-2' style={{ color: sendNow ? '#28a745' : 'inherit', fontWeight: sendNow ? 600 : 400 }}>
                Enviar imediatamente
              </Label>
            </div>
            <div className='d-flex align-items-center'>
              <Input
                type='checkbox'
                id='hasAttachment'
                style={{ position: 'relative', marginLeft: 0 }}
                checked={showAttachment}
                onChange={(e) => setShowAttachment(e.target.checked)}
              />
              <Label for='hasAttachment' className='mb-0 ml-2'>Incluir anexo</Label>
            </div>
          </div>

          {showAttachment && (
            <div
              style={{
                padding: '12px 16px',
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '0.375rem',
                marginBottom: '16px',
              }}
            >
              <input
                ref={fileInputRef}
                type='file'
                accept='.pdf,image/jpeg,image/png,image/webp,image/gif'
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              {!form.attachmentUrl ? (
                <div className='d-flex align-items-center' style={{ gap: '12px' }}>
                  <Button
                    color='primary'
                    size='sm'
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <><Spinner size='sm' className='mr-1' /> Enviando...</>
                    ) : (
                      <><i className='ni ni-cloud-upload-96 mr-1' /> Selecionar arquivo</>
                    )}
                  </Button>
                  <span style={{ fontSize: '12px', color: '#adb5bd' }}>PDF ou imagem (máx. 20 MB)</span>
                </div>
              ) : (
                <div className='d-flex align-items-center' style={{ gap: '12px' }}>
                  <div
                    style={{
                      height: '48px', width: '48px', flexShrink: 0, borderRadius: '4px',
                      border: '1px solid #dee2e6', overflow: 'hidden',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: '#f8f9fa',
                    }}
                  >
                    {form.attachmentType && form.attachmentType.startsWith('image/') ? (
                      <img
                        src={form.attachmentUrl}
                        alt='preview'
                        crossOrigin='anonymous'
                        style={{ height: '100%', width: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        display: form.attachmentType && form.attachmentType.startsWith('image/') ? 'none' : 'flex',
                        alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%',
                      }}
                    >
                      <i className='ni ni-single-copy-04 text-danger' style={{ fontSize: '20px' }} />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {form.attachmentName}
                    </div>
                    <div style={{ fontSize: '11px', color: '#adb5bd' }}>{form.attachmentType}</div>
                  </div>
                  <Button
                    color='link'
                    size='sm'
                    className='text-danger p-0'
                    title='Remover anexo'
                    onClick={() => setForm((prev) => ({ ...prev, attachmentUrl: '', attachmentType: '', attachmentName: '' }))}
                  >
                    <i className='ni ni-fat-remove' />
                  </Button>
                  <Button
                    color='link'
                    size='sm'
                    className='p-0'
                    title='Trocar arquivo'
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={uploading}
                  >
                    <i className='ni ni-refresh-02' />
                  </Button>
                </div>
              )}
            </div>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color='secondary' onClick={toggle}>Cancelar</Button>
        <Button
          color='success'
          onClick={() => handleSubmit()}
          disabled={!isValid}
        >
          {sendNow ? 'Enviar agora' : 'Agendar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
